import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { createMessage } from "../../firebase/messageService";

// Validation schema
const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name is too short")
    .max(50, "Name is too long")
    .required("Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  subject: Yup.string()
    .min(2, "Subject is too short")
    .max(100, "Subject is too long")
    .required("Subject is required"),
  message: Yup.string()
    .min(10, "Message is too short")
    .max(1000, "Message is too long")
    .required("Message is required"),
});

const ContactForm: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: any, { resetForm }: any) => {
    setIsSubmitting(true);
    try {
      // Use the messageService to create a new message
      await createMessage({
        ...values,
        read: false,
        archived: false,
      });

      toast.success("Message sent successfully!");
      resetForm();
    } catch (error) {
      toast.error("Failed to send message. Please try again later.");
      console.error("Error sending message:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{ name: "", email: "", subject: "", message: "" }}
      validationSchema={ContactSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, dirty }) => (
        <Form className="space-y-4">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Name
            </label>
            <Field
              type="text"
              name="name"
              id="name"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
              placeholder="Your name"
            />
            <ErrorMessage
              name="name"
              component="div"
              className="mt-1 text-sm text-red-600 dark:text-red-400"
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Email
            </label>
            <Field
              type="email"
              name="email"
              id="email"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
              placeholder="Your email"
            />
            <ErrorMessage
              name="email"
              component="div"
              className="mt-1 text-sm text-red-600 dark:text-red-400"
            />
          </div>

          <div>
            <label
              htmlFor="subject"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Subject
            </label>
            <Field
              type="text"
              name="subject"
              id="subject"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
              placeholder="Message subject"
            />
            <ErrorMessage
              name="subject"
              component="div"
              className="mt-1 text-sm text-red-600 dark:text-red-400"
            />
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Message
            </label>
            <Field
              as="textarea"
              name="message"
              id="message"
              rows={5}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
              placeholder="Your message"
            />
            <ErrorMessage
              name="message"
              component="div"
              className="mt-1 text-sm text-red-600 dark:text-red-400"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !(isValid && dirty)}
            className={`w-full py-3 px-4 text-white bg-primary-600 font-medium rounded-md hover:bg-primary-700 transition-colors ${
              isSubmitting || !(isValid && dirty)
                ? "opacity-60 cursor-not-allowed"
                : ""
            }`}
          >
            {isSubmitting ? "Sending..." : "Send Message"}
          </button>
        </Form>
      )}
    </Formik>
  );
};

export default ContactForm;
